// TypeScript Data Layer for Shalimar ERP

export interface Company {
  name: string;
  tagline: string;
  logo: string;
  version: string;
  updatedAt: string;
}

export interface Stat {
  value: number;
  label: string;
  change: number;
  unit: string;
}

export interface Stats {
  totalRevenue: Stat;
  totalEvents: Stat;
  pendingEnquiries: Stat;
  netProfit: Stat;
  confirmedEvents: Stat;
  vendorsPaid: Stat;
  pendingInvoices: Stat;
  avgEventValue: Stat;
}

export interface Event {
  id: string;
  client: string;
  type: string;
  date: string;
  guests: number;
  status: 'confirmed' | 'draft' | 'quoted' | 'completed';
  revenue: number;
  cost: number;
  profit: number;
  package: string;
  advance: number;
  balance: number;
}

export interface Enquiry {
  id: string;
  name: string;
  phone: string;
  email: string;
  eventDate: string;
  guests: number;
  type: string;
  status: 'new' | 'qualified' | 'converted' | 'rejected';
  notes: string;
  receivedAt: string;
  leadSource: string;
}

export interface Vendor {
  id: string;
  name: string;
  type: string;
  contact: string;
  email: string;
  status: 'active' | 'inactive';
  totalAssigned: number;
  totalPaid: number;
}

export interface Invoice {
  id: string;
  event: string;
  client: string;
  total: number;
  discount: number;
  final: number;
  advance: number;
  balance: number;
  status: 'partial' | 'paid' | 'pending';
  date: string;
  dueDate: string;
}

export interface Task {
  id: string;
  event: string;
  title: string;
  assignedTo: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed';
}

export interface Room {
  id: string;
  name: string;
  type: 'Banquet' | 'Open Air' | 'Conference' | 'Accommodation';
  capacity: number;
  rate: number;
  status: 'available' | 'booked';
}

export interface Activity {
  id: number;
  action: string;
  detail: string;
  time: string;
  icon: string;
  type: string;
}

export interface Package {
  id: string;
  name: string;
  type: string;
  basePrice: number;
  services: number;
  bookings: number;
}

export interface Staff {
  id: string;
  name: string;
  role: string;
  email: string;
  status: 'active' | 'inactive';
  tasks: number;
}

export interface Quotation {
  id: string;
  client: string;
  enquiryId?: string;
  eventType: string;
  guests: number;
  eventDate: string;
  package: string;
  venue: string;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  status: 'draft' | 'sent' | 'approved' | 'rejected' | 'expired';
  validUntil: string;
  notes: string;
  createdAt: string;
}

export interface NavItem {
  label: string;
  icon: string;
  href: string;
  page: string;
}

export interface RevenueChartItem {
  month: string;
  revenue: number;
  cost: number;
  profit: number;
}

export interface EventType {
  type: string;
  count: number;
  percent: number;
  color: string;
}

export const company: Company = {
  name: "Shalimar",
  tagline: "Premium Event Management ERP",
  logo: "✦",
  version: "v1.0",
  updatedAt: "March 2026"
};

export const stats: Stats = {} as Stats;
export const events: Event[] = [];
export const enquiries: Enquiry[] = [];
export const vendors: Vendor[] = [];
export const invoices: Invoice[] = [];
export const tasks: Task[] = [];
export const rooms: Room[] = [];
export const revenueChart: RevenueChartItem[] = [];
export const eventTypes: EventType[] = [];
export const activities: Activity[] = [];
export const packages: Package[] = [];
export const staff: Staff[] = [];
export const quotations: Quotation[] = [];

export const nav: NavItem[] = [
  { label: "Dashboard", icon: "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z", href: "/", page: "dashboard" },
  { label: "Enquiries", icon: "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z", href: "/enquiries", page: "enquiries" },
  { label: "Quotations", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", href: "/quotations", page: "quotations" },
  { label: "Events", icon: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z", href: "/events", page: "events" },
  { label: "Invoices", icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", href: "/invoices", page: "invoices" },
  { label: "Vendors", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z", href: "/vendors", page: "vendors" },
  { label: "Tasks", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4", href: "/tasks", page: "tasks" },
  { label: "Rooms", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4", href: "/rooms", page: "rooms" },
  { label: "Packages", icon: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4", href: "/packages", page: "packages" },
  { label: "Staff", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z", href: "/staff", page: "staff" },
  { label: "Reports", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z", href: "/reports", page: "reports" },
  { label: "Settings", icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37 1.608.922 2.918.922 2.918.922", href: "/settings", page: "settings" }
];

// Helper functions

/**
 * formatCurrencyShort — compact display for chart Y-axes and tight spaces only.
 * e.g. ₹6,00,000 → "₹6.0L" | ₹25,000 → "₹25K"
 * ⚠️ DO NOT use in tables, modals, ledgers, invoices, or any financial detail view.
 */
export const formatCurrencyShort = (amount: number | undefined | null): string => {
  if (amount == null || isNaN(amount)) return "₹0";
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
  return `₹${amount.toLocaleString("en-IN")}`;
};

/**
 * formatCurrencyFull — always shows the exact amount in Indian locale.
 * e.g. ₹6,00,000 — use everywhere (tables, cards, modals, invoices, toasts).
 */
export const formatCurrencyFull = (amount: number | undefined | null): string => {
  if (amount == null || isNaN(amount)) return "₹0";
  return `₹${amount.toLocaleString("en-IN")}`;
};

/**
 * formatCurrency — alias for formatCurrencyFull.
 * Always shows the full exact amount. Safe to use everywhere.
 */
export const formatCurrency = formatCurrencyFull;

// Date formatting now lives in a single canonical module (lib/dateFormat.ts).
// Re-exported here so existing `import { formatDate, ... } from "@/lib/data"`
// call-sites keep working while routing through one Indian-format source of truth.
export {
  formatDate,
  formatDateTime,
  formatTime,
  formatDateLong,
  formatMonthYear,
  formatMonthShort,
  formatWeekdayShort,
  formatDateNumeric,
  localToday,
  formatDateForInput,
} from "./dateFormat";

export const statusBadge = (status: string): string => {
  const map: Record<string, string> = {
    confirmed: "bg-emerald-100 text-emerald-700 border border-emerald-200",
    completed: "bg-slate-100 text-slate-600 border border-slate-200",
    draft: "bg-indigo-100 text-indigo-700 border border-indigo-200",
    quoted: "bg-amber-100 text-amber-700 border border-amber-200",
    new: "bg-sky-100 text-sky-700 border border-sky-200",
    qualified: "bg-violet-100 text-violet-700 border border-violet-200",
    converted: "bg-emerald-100 text-emerald-700 border border-emerald-200",
    rejected: "bg-red-100 text-red-700 border border-red-200",
    active: "bg-emerald-100 text-emerald-700 border border-emerald-200",
    inactive: "bg-slate-100 text-slate-500 border border-slate-200",
    partial: "bg-amber-100 text-amber-700 border border-amber-200",
    paid: "bg-emerald-100 text-emerald-700 border border-emerald-200",
    pending: "bg-red-100 text-red-700 border border-red-200",
    in_progress: "bg-blue-100 text-blue-700 border border-blue-200",
    available: "bg-emerald-100 text-emerald-700 border border-emerald-200",
    booked: "bg-rose-100 text-rose-700 border border-rose-200",
    high: "bg-red-100 text-red-700 border border-red-200",
    medium: "bg-amber-100 text-amber-700 border border-amber-200",
    low: "bg-slate-100 text-slate-600 border border-slate-200"
  };
  return map[status] || "bg-gray-100 text-gray-600 border border-gray-200";
};
